import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, BellSlash, TestTube } from "@phosphor-icons/react";
import { toast } from "sonner";

// Convert base64url VAPID public key → Uint8Array for the browser.
function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const canPush = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

export default function PushToggle() {
  const { user } = useAuth();
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publicKey, setPublicKey] = useState("");

  useEffect(() => {
    if (!canPush()) return;
    setSupported(true);
    http.get("/push/public-key").then((r) => setPublicKey(r.data?.public_key || "")).catch(() => {});
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((sub) => setSubscribed(!!sub))
    ).catch(() => {});
  }, []);

  if (!supported || !user) return null;

  const subscribe = async () => {
    if (busy || !publicKey) { toast.error("Notifications aren't configured yet."); return; }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications denied."); setBusy(false); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await http.post("/push/subscribe", {
        endpoint: sub.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh")))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
          auth:   btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth")))  ).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
        },
        ua: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notifications on. We'll ping you sparingly.");
    } catch (e) { toast.error("Couldn't subscribe."); }
    finally { setBusy(false); }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await http.delete(`/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.info("Notifications off.");
    } catch { toast.error("Couldn't unsubscribe."); }
    finally { setBusy(false); }
  };

  const test = async () => {
    try {
      await http.post("/push/test", { title: "NaturePulse", body: "Ping — you're all set." });
      toast.success("Test ping sent.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed."); }
  };

  return (
    <div className="inline-flex items-center gap-2" data-testid="push-toggle">
      <button
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={busy}
        data-testid={subscribed ? "push-unsubscribe" : "push-subscribe"}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition-colors ${
          subscribed
            ? "border border-emerald-600 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
            : "border border-[color:var(--np-brand,#B85F2A)] text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white"
        }`}
      >
        {subscribed ? <><Bell size={12} weight="fill" /> Notifications on</> : <><BellSlash size={12} weight="fill" /> Enable notifications</>}
      </button>
      {subscribed && (
        <button
          onClick={test}
          data-testid="push-test"
          className="inline-flex items-center gap-1 rounded-full border border-black/25 px-3 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#1B4332] hover:text-[#F5EBDC]"
        >
          <TestTube size={12} weight="fill" /> Test
        </button>
      )}
    </div>
  );
}
